import { useTranslation } from 'react-i18next';
import { HpButton, HpMultiselect } from '@hp/ui';
import { calendarDays } from '@hp/domain';
import type { FilterOptions } from './use-report';
import { COMPARE, PRESETS, presetRange, type Compare, type DashboardFilter, type Preset } from './filters';
import { fmtDate } from './format';

const CHANNELS = ['meta', 'google', 'sklik', 'email', 'linkedin', 'portals', 'web', 'own', 'unassigned'] as const;

/** The controls card (`pa-controls`): presets, dates, comparison, three multiselects and the active-range line. */
export function FilterBar({
  filter,
  onChange,
  onReset,
  options,
  today,
  active,
}: {
  filter: DashboardFilter;
  onChange: (next: DashboardFilter) => void;
  onReset: () => void;
  options: FilterOptions | undefined;
  today: string;
  active: boolean;
}) {
  const { t } = useTranslation(['performance', 'common']);
  const valid = filter.from <= filter.to && filter.to <= today;
  const days = valid ? calendarDays(filter.from, filter.to) : 0;
  const setPreset = (p: Preset) => onChange({ ...filter, preset: p, ...(p === 'custom' ? {} : presetRange(p, today)) });
  /** Search + "only active" + "select all shown" of the long CRM pickers (HPD-27); `title` explains "active". */
  const narrowing = (title: string) => ({
    searchPlaceholder: t('dashboard.filters.search'),
    activeOnlyLabel: t('dashboard.filters.onlyActive'),
    activeOnlyTitle: title,
    selectShownLabel: t('dashboard.filters.selectShown'),
    emptyLabel: t('dashboard.filters.noMatches'),
  });

  return (
    <section className="pa-controls hp-card" aria-label={t('dashboard.filters.aria')}>
      <div className="pa-controls-top">
        <div className="pa-presets">
          <span>{t('dashboard.filters.period')}</span>
          {PRESETS.map((p) => (
            <button
              key={p}
              type="button"
              className={filter.preset === p ? 'active' : ''}
              aria-pressed={filter.preset === p}
              onClick={() => setPreset(p)}
            >
              {t(`dashboard.filters.${p}`)}
            </button>
          ))}
        </div>
      </div>
      <div className="pa-filter-grid">
        <label className="pa-field hp-field">
          <span>{t('dashboard.filters.from')}</span>
          <input
            type="date"
            value={filter.from}
            max={filter.to}
            onChange={(e) => onChange({ ...filter, preset: 'custom', from: e.target.value })}
          />
        </label>
        <label className="pa-field hp-field">
          <span>{t('dashboard.filters.to')}</span>
          <input
            type="date"
            value={filter.to}
            min={filter.from}
            max={today}
            onChange={(e) => onChange({ ...filter, preset: 'custom', to: e.target.value })}
          />
        </label>
        <label className="pa-field hp-field">
          <span>{t('dashboard.filters.compare')}</span>
          <select value={filter.compare} onChange={(e) => onChange({ ...filter, compare: e.target.value as Compare })}>
            {COMPARE.map((c) => (
              <option key={c} value={c}>
                {t(`dashboard.filters.compare${c === 'previous' ? 'Previous' : c === 'year' ? 'Year' : 'None'}`)}
              </option>
            ))}
          </select>
        </label>
        <HpMultiselect
          label={t('dashboard.filters.project')}
          allLabel={t('dashboard.filters.allProjects')}
          heading={t('dashboard.filters.heading')}
          hint={t('dashboard.filters.hint')}
          items={(options?.projects ?? []).map((p) => ({
            value: p.id,
            label: p.label,
            description: p.projectName ?? t('mapping.casesCount', { count: p.cases }),
            active: p.active,
          }))}
          selected={filter.projects}
          onChange={(projects) => onChange({ ...filter, projects })}
          {...narrowing(t('dashboard.filters.activeProjectsTitle', { days: options?.activeDays ?? 30 }))}
        />
        <HpMultiselect
          label={t('dashboard.filters.channel')}
          allLabel={t('dashboard.filters.allChannels')}
          heading={t('dashboard.filters.heading')}
          hint={t('dashboard.filters.hint')}
          items={CHANNELS.map((c) => ({ value: c, label: t(`channel.${c}`) }))}
          selected={filter.channels}
          onChange={(channels) => onChange({ ...filter, channels })}
        />
        <HpMultiselect
          label={t('dashboard.filters.campaign')}
          allLabel={t('dashboard.filters.allCampaigns')}
          heading={t('dashboard.filters.heading')}
          hint={t('dashboard.filters.hint')}
          items={(options?.campaigns ?? []).map((c) => ({
            value: c.id,
            label: c.label,
            description: t('mapping.casesCount', { count: c.cases }),
            active: c.active,
          }))}
          selected={filter.campaigns}
          onChange={(campaigns) => onChange({ ...filter, campaigns })}
          {...narrowing(t('dashboard.filters.activeCampaignsTitle', { days: options?.activeDays ?? 30 }))}
        />
      </div>
      {!valid && (
        <div className="pa-error" role="alert">
          {t('dashboard.filters.invalid')}
        </div>
      )}
      <div className="pa-active-range">
        <span className="pa-live-dot" />
        <strong>
          {fmtDate(filter.from)} — {fmtDate(filter.to)}
        </strong>
        <span>{t('dashboard.filters.days', { count: days })}</span>
        <span className="pa-comparison-caption">
          {t(
            `dashboard.filters.caption${filter.compare === 'previous' ? 'Previous' : filter.compare === 'year' ? 'Year' : 'None'}`,
          )}
        </span>
        {active && (
          <HpButton variant="quiet" icon="rotateCcw" className="pa-reset" onClick={onReset}>
            {t('dashboard.filters.reset')}
          </HpButton>
        )}
      </div>
    </section>
  );
}
